import type { ShareChannel } from '#src/app/shared/hooks/share';

/**
 * How a channel's share action moves the browser — which is what decides whether a post-share
 * redirect may navigate the current tab while that share is still in flight.
 *
 * Only load-bearing inside webviews (Facebook's in-app browser, iOS in-app browsers, Android
 * WebView, iOS home-screen PWAs — everything `userAgent.isWebview` covers). In a regular browser
 * every channel either opens its own tab or hands off to another app, so a redirect never contends
 * with it.
 */
type ShareNavigationMode =
	/** Never navigates: a clipboard write and/or `navigator.share()`. The page stays exactly where it is. */
	| 'none'
	/**
	 * Opens a non-http scheme (`sms:`, `mailto:`, `line://`, `fb-messenger://`) the OS hands to another
	 * app. Whether the anchor also targets a new tab varies by channel and does not matter here: either
	 * way this tab keeps its document, because the scheme never resolves to a page.
	 */
	| 'appHandoff'
	/** Opens an http(s) URL in a new tab via an anchor with `target="_blank"`; the current tab is untouched. */
	| 'newTab'
	/**
	 * Navigates this tab to an http(s) page, unloading the document, so a redirect racing it replaces
	 * the share mid-flight. No channel below is classified this way: the one share that navigates the
	 * current tab to a page is `shareFacebook`'s `urlSharer: 'sharer'` path on mobile, and only the
	 * starter pages pass that option — they never call `canRedirectAfterShare`.
	 */
	| 'currentTab';

/**
 * Keyed by `ShareChannel` so a new channel cannot be added without classifying it. Each entry
 * describes what `#src/app/shared/share/<channel>/share.ts` actually does; keep them in sync.
 */
const SHARE_NAVIGATION_MODES: Readonly<Record<ShareChannel, ShareNavigationMode>> = {
	copy: 'none',
	instagram: 'none',
	instagramStories: 'none',
	tiktok: 'none',
	mobileNativeShare: 'none',

	email: 'appHandoff',
	sms: 'appHandoff',
	facebookMessenger: 'appHandoff',
	line: 'appHandoff',

	twitter: 'newTab',
	/**
	 * PSF shares with `mode.mobile: 'url'` and no `urlSharer`, which opens one of the app_id-gated
	 * dialog endpoints in its own tab — a page the sharer interacts with and returns from.
	 */
	facebook: 'newTab',
	whatsapp: 'newTab',
	reddit: 'newTab',
	vk: 'newTab',
	nextdoor: 'newTab',
};

type Options = {
	channel: ShareChannel;
	isWebview: boolean;
};

/**
 * Whether a post-share redirect may navigate the current tab now that `channel` has shared.
 *
 * Every channel currently keeps its document — none is classified `currentTab` — so this permits the
 * redirect throughout. It stays the single place that decides, because the keyed record above and the
 * exhaustive switch below force a new channel, or a share rewritten to leave the tab, to state which
 * it is rather than inheriting permission.
 *
 * Suppressing the redirect for a channel that doesn't need it strands the supporter on
 * `/psf/promote_or_share` (or on share page 1) after a share that already succeeded, so widen the
 * suppressed set only with evidence from a real device.
 *
 * Modes are allowed one by one rather than excluding the unsafe ones: a mode added later must state
 * that it is safe instead of inheriting it. Omitting a case here fails to compile.
 */
export function canRedirectAfterShare({ channel, isWebview }: Options): boolean {
	if (!isWebview) return true;

	const mode = SHARE_NAVIGATION_MODES[channel];
	switch (mode) {
		case 'none':
		case 'appHandoff':
		case 'newTab':
			return true;
		case 'currentTab':
			return false;
		default: {
			// A mode added without a case above reaches here, where `never` stops the build.
			const unhandled: never = mode;
			return unhandled;
		}
	}
}
